import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import {
  defaultMasterFlowUiConfig,
  parseMasterFlowUiConfig,
} from '../../services/masterFlowUiConfig';

const denseCheckSx = {
  m: 0,
  mr: 0,
  py: 0,
  '& .MuiCheckbox-root': { p: 0.4 },
  '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', lineHeight: 1.3 },
};

/**
 * Compact ⋮ menu config from app_lookup (FLOW_UI_ACTION / FLOW_UI_SECTION).
 * Properties nests drawer subsections (like Sync/Hub).
 */
const MasterFlowUiConfigFields = ({
  value,
  onChange,
  actions = [],
  sections = [],
  surfaceKind,
}) => {
  const catalog = useMemo(() => ({ actions, sections }), [actions, sections]);
  const config = parseMasterFlowUiConfig(value || defaultMasterFlowUiConfig(catalog), catalog);
  const propertiesDef = actions.find((a) => a.key === 'properties');
  const otherActions = actions.filter((a) => a.key !== 'properties');
  const propertiesOn = config.actions.properties !== false;
  const kindLabel =
    surfaceKind === 'HUB' ? 'Hub' : surfaceKind === 'SYNC' ? 'Sync' : null;

  const setAction = (key, checked) => {
    onChange({
      ...config,
      version: 1,
      actions: { ...config.actions, [key]: checked },
    });
  };

  const setSection = (key, checked) => {
    onChange({
      ...config,
      version: 1,
      sections: { ...config.sections, [key]: checked },
    });
  };

  const setAllSections = (checked) => {
    onChange({
      ...config,
      version: 1,
      sections: Object.fromEntries(sections.map(({ key }) => [key, checked])),
    });
  };

  if (actions.length === 0 && sections.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No Flow UI options in lookup. Seed FLOW_UI_ACTION_COMMON / _SYNC / _HUB (and SECTION_*) then
        refresh.
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {kindLabel ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 0.75, fontSize: '0.7rem' }}
        >
          Common + {kindLabel}-only options from lookup
        </Typography>
      ) : null}
      {propertiesDef && (
        <>
          <FormControlLabel
            sx={denseCheckSx}
            control={
              <Checkbox
                size="small"
                checked={propertiesOn}
                onChange={(e) => setAction('properties', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8125rem' }}>
                {propertiesDef.label}
              </Typography>
            }
          />

          {sections.length > 0 && (
            <Box
              sx={{
                ml: 2.75,
                mt: 0.25,
                mb: 0.75,
                pl: 1.25,
                borderLeft: '2px solid',
                borderColor: propertiesOn ? 'divider' : 'action.disabledBackground',
                opacity: propertiesOn ? 1 : 0.4,
                pointerEvents: propertiesOn ? 'auto' : 'none',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.25}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem', letterSpacing: 0.2 }}
                >
                  Subsections
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    underline="hover"
                    disabled={!propertiesOn}
                    onClick={() => setAllSections(true)}
                    sx={{ fontSize: '0.7rem', cursor: 'pointer' }}
                  >
                    All
                  </Link>
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    underline="hover"
                    disabled={!propertiesOn}
                    onClick={() => setAllSections(false)}
                    sx={{ fontSize: '0.7rem', cursor: 'pointer' }}
                  >
                    None
                  </Link>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  columnGap: 0.5,
                  rowGap: 0,
                }}
              >
                {sections.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    sx={denseCheckSx}
                    control={
                      <Checkbox
                        size="small"
                        checked={config.sections[key] !== false}
                        onChange={(e) => setSection(key, e.target.checked)}
                        disabled={!propertiesOn}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      {otherActions.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            columnGap: 0.5,
            rowGap: 0,
            pt: propertiesDef ? 0.5 : 0,
            borderTop: propertiesDef ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          {otherActions.map(({ key, label }) => (
            <FormControlLabel
              key={key}
              sx={denseCheckSx}
              control={
                <Checkbox
                  size="small"
                  checked={config.actions[key] !== false}
                  onChange={(e) => setAction(key, e.target.checked)}
                />
              }
              label={label}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MasterFlowUiConfigFields;
